import { Box } from '@mui/material';
import { FileTextIcon } from '@phosphor-icons/react/dist/ssr/FileText';
import { GearIcon } from '@phosphor-icons/react/dist/ssr/Gear';
import { HandHeartIcon } from '@phosphor-icons/react/dist/ssr/HandHeart';
import { LightbulbIcon } from '@phosphor-icons/react/dist/ssr/Lightbulb';
import { UsersIcon } from '@phosphor-icons/react/dist/ssr/Users';
import { NavigationBar, vars } from '@tgtg/kit';
import { CharityVettingStatus } from 'api/users/types';
import useCurrentUserRoles from 'api/users/useCurrentUserRoles';
import useCharityVettingStatus from 'common/auth/useCharityVettingStatus';
import useNumberOfPendingDonations from 'features/donations/hooks/useNumberOfPendingDonations';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import {
  DOCUMENTS_PATH,
  DONATIONS_PATH,
  HELP_CENTER_PATH,
  SETTINGS_PATH,
  USERS_PATH,
  VETTING_PROGRESS_PATH,
} from 'src/core/paths/paths';

import msg from '../messages';

import BottomBarMorePopover from './BottomBarMorePopover';
import { isItemActive } from './navItemPath';

import type { NavigationBarItem } from '@tgtg/kit';

export const MAX_BOTTOM_BAR_ITEMS = 4;

export const splitNavItems = (items: NavigationBarItem[]) =>
  items.length <= MAX_BOTTOM_BAR_ITEMS
    ? { visibleItems: items, overflowItems: [] }
    : {
        visibleItems: items.slice(0, MAX_BOTTOM_BAR_ITEMS - 1),
        overflowItems: items.slice(MAX_BOTTOM_BAR_ITEMS - 1),
      };

const BottomBar = () => {
  const location = useLocation();
  const numberOfDonationsToConfirm = useNumberOfPendingDonations();
  const { formatMessage } = useIntl();
  const { charityVettingStatus } = useCharityVettingStatus();
  const { isCollector } = useCurrentUserRoles();
  const isVetted = charityVettingStatus === CharityVettingStatus.Vetted;

  // Users and Documents are both behind RequireCoordinatorOrLegalRep, so they
  // stay hidden for collectors (matching the desktop drawer)
  const coordinatorItems: NavigationBarItem[] = isCollector
    ? []
    : [
        {
          id: 'users',
          to: USERS_PATH,
          label: formatMessage(msg.users),
          icon: <UsersIcon />,
        },
        {
          id: 'documents',
          to: DOCUMENTS_PATH,
          activeForPaths: [/^\/documents(\/|$)/],
          label: formatMessage(msg.documents),
          icon: <FileTextIcon />,
        },
      ];

  const helpCenterItem: NavigationBarItem = {
    id: 'help-center',
    to: HELP_CENTER_PATH,
    label: formatMessage(msg.helpCenter),
    icon: <LightbulbIcon />,
  };

  const settingsItem: NavigationBarItem = {
    id: 'settings',
    to: SETTINGS_PATH,
    label: formatMessage(msg.settings),
    icon: <GearIcon />,
  };

  const items: NavigationBarItem[] = isVetted
    ? [
        {
          id: 'donations',
          to: DONATIONS_PATH,
          label: formatMessage(msg.donations),
          icon: <HandHeartIcon />,
          badge:
            numberOfDonationsToConfirm > 0
              ? { variant: 'number', value: numberOfDonationsToConfirm }
              : undefined,
        },
        ...coordinatorItems,
        helpCenterItem,
        settingsItem,
      ]
    : [
        {
          id: 'vetting-progress',
          to: VETTING_PROGRESS_PATH,
          label: formatMessage(msg.yourApplication),
          icon: <FileTextIcon />,
        },
        helpCenterItem,
        settingsItem,
      ];

  const { visibleItems, overflowItems } = splitNavItems(items);
  const hasOverflow = overflowItems.length > 0;
  const isOverflowActive = overflowItems.some((item) => isItemActive(item, location.pathname));

  if (!hasOverflow) {
    return (
      <NavigationBar
        items={visibleItems}
        currentPathname={location.pathname}
        aria-label={formatMessage(msg.navigationA11y)}
      />
    );
  }

  return (
    <Box
      // Values mirrored from the kit's own bottom nav.
      sx={{
        display: 'flex',
        gap: vars.spacing.neutral.xsmall,
        alignItems: 'center',
        width: '100%',
        background: vars.color.surface.base,
        boxShadow: '0 0 12px #0000000d',
        paddingBlockEnd: vars.spacing.neutral.large,
        '& > nav': {
          flex: visibleItems.length,
          width: 'auto',
          background: 'none',
          boxShadow: 'none',
          paddingBlockEnd: 0,
        },
      }}
    >
      <NavigationBar
        items={visibleItems}
        currentPathname={location.pathname}
        aria-label={formatMessage(msg.navigationA11y)}
      />
      <BottomBarMorePopover items={overflowItems} isActive={isOverflowActive} />
    </Box>
  );
};

export default BottomBar;
